import { CheckCircle, XCircle, Clock, RotateCcw } from 'lucide-react'
import type { ApprovalEntry } from '@/lib/api'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

const FINANCE_ADMIN = ['super_admin', 'admin', 'finance']

interface ApprovalsPanelProps {
  approvals: ApprovalEntry[]
  additionalApprovers: string[]
  currentUsername: string
  currentRole: string
  isPending: boolean
  rejectMode: boolean
  rejectReason: string
  onRejectReasonChange: (v: string) => void
  onApprove: () => void
  onReject: (reason?: string) => void
  onRevoke: () => void
  onToggleRejectMode: () => void
  onCancelReject: () => void
  isApprovePending: boolean
  isRejectPending: boolean
  isRevokePending: boolean
}

function roleLabel(role: string): string {
  if (role === 'super_admin') return 'Super Admin'
  if (role === 'admin') return 'Admin'
  if (role === 'finance') return 'Finance'
  if (role === 'chief_engineer') return 'Chief Engineer'
  if (role === 'engineer') return 'Engineer'
  if (role === 'sales') return 'Sales'
  return role
}

interface RowProps {
  name: string
  role?: string
  entry?: ApprovalEntry
  isSelf: boolean
  isPending: boolean
  onApprove?: () => void
  onToggleRejectMode?: () => void
  onRevoke?: () => void
  isApprovePending?: boolean
  isRevokePending?: boolean
}

function ApproverRow({ name, role, entry, isSelf, isPending, onApprove, onToggleRejectMode, onRevoke, isApprovePending, isRevokePending }: RowProps) {
  const displayRole = entry?.approver_role ? roleLabel(entry.approver_role) : (role ? roleLabel(role) : null)

  return (
    <div className="flex items-center justify-between py-2.5 border-b border-slate-100 last:border-0">
      <div className="flex items-center gap-2 min-w-0">
        {entry?.action === 'approved' && <CheckCircle size={14} className="text-green-600 shrink-0" />}
        {entry?.action === 'rejected' && <XCircle size={14} className="text-red-600 shrink-0" />}
        {!entry && <Clock size={14} className="text-slate-400 shrink-0" />}
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium text-slate-800">{entry?.approver_name || name}</span>
            {displayRole && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{displayRole}</span>
            )}
          </div>
          {entry ? (
            <span className="text-xs text-slate-500">
              {entry.action === 'approved' ? 'Approved' : 'Rejected'}{' '}
              {formatMyanmarDateTime(entry.at)}
            </span>
          ) : (
            <span className="text-xs text-slate-400">Awaiting approval</span>
          )}
        </div>
      </div>

      {isSelf && (
        <div className="flex gap-1.5 shrink-0 ml-2">
          {!entry && isPending && (
            <>
              <button
                onClick={onApprove}
                disabled={isApprovePending}
                className="px-2.5 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-lg hover:bg-green-200 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={onToggleRejectMode}
                className="px-2.5 py-1 text-xs font-medium bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Reject
              </button>
            </>
          )}
          {entry && (
            <button
              onClick={onRevoke}
              disabled={isRevokePending}
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 disabled:opacity-50"
            >
              <RotateCcw size={11} />
              Revoke
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default function ApprovalsPanel({
  approvals,
  additionalApprovers,
  currentUsername,
  currentRole,
  isPending,
  rejectMode,
  rejectReason,
  onRejectReasonChange,
  onApprove,
  onReject,
  onRevoke,
  onToggleRejectMode,
  onCancelReject,
  isApprovePending,
  isRejectPending,
  isRevokePending,
}: ApprovalsPanelProps) {
  const isFinanceAdmin = FINANCE_ADMIN.includes(currentRole)
  const isAdditionalApprover = additionalApprovers.includes(currentUsername)

  // Finance/admin approvals from the approvals array
  const financeApprovals = approvals.filter(a => FINANCE_ADMIN.includes(a.approver_role))
  // The latest finance/admin action (if any — this is the shared "finance slot")
  const latestFinanceEntry = financeApprovals.length > 0 ? financeApprovals[financeApprovals.length - 1] : undefined
  // Current user's own entry (if they are finance/admin)
  const myFinanceEntry = isFinanceAdmin ? approvals.find(a => a.approver_username === currentUsername) : undefined
  // Is the current finance/admin user the one who owns the latest entry?
  const iAmTheFinanceSlot = isFinanceAdmin && (
    !latestFinanceEntry || latestFinanceEntry.approver_username === currentUsername
  )

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="bg-slate-50 px-3 py-2 border-b border-slate-200">
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Approvals</h3>
      </div>
      <div className="px-3 divide-y divide-slate-100">

        {/* Finance/Admin slot — always shown.
            If the current user is finance/admin AND is the slot owner (or no one has acted yet),
            show interactive row. Otherwise show the latest finance entry read-only. */}
        {iAmTheFinanceSlot ? (
          <ApproverRow
            name={currentUsername}
            role={currentRole}
            entry={myFinanceEntry}
            isSelf={true}
            isPending={isPending}
            onApprove={onApprove}
            onRevoke={onRevoke}
            onToggleRejectMode={onToggleRejectMode}
            isApprovePending={isApprovePending}
            isRevokePending={isRevokePending}
          />
        ) : latestFinanceEntry ? (
          // Another finance/admin has already acted — show their entry (read-only)
          <ApproverRow
            name={latestFinanceEntry.approver_name}
            role={latestFinanceEntry.approver_role}
            entry={latestFinanceEntry}
            isSelf={false}
            isPending={isPending}
          />
        ) : (
          // No finance action yet — show generic pending finance slot
          <ApproverRow
            name="Finance / Admin"
            role="Finance / Admin"
            entry={undefined}
            isSelf={false}
            isPending={isPending}
          />
        )}

        {/* Additional approver rows — always shown */}
        {additionalApprovers.map(username => {
          const entry = approvals.find(a => a.approver_username === username)
          const isSelf = username === currentUsername
          return (
            <ApproverRow
              key={username}
              name={entry?.approver_name || username}
              role={entry?.approver_role}
              entry={entry}
              isSelf={isSelf}
              isPending={isPending}
              onApprove={onApprove}
              onRevoke={onRevoke}
              onToggleRejectMode={onToggleRejectMode}
              isApprovePending={isApprovePending}
              isRevokePending={isRevokePending}
            />
          )
        })}
      </div>

      {/* Reject reason form */}
      {rejectMode && (isFinanceAdmin || isAdditionalApprover) && (
        <div className="bg-red-50 border-t border-red-200 p-3 space-y-2">
          <textarea
            rows={2}
            value={rejectReason}
            onChange={e => onRejectReasonChange(e.target.value)}
            placeholder="Reason for rejection (optional)..."
            className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700 resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => onReject(rejectReason || undefined)}
              disabled={isRejectPending}
              className="flex-1 py-1.5 text-xs font-medium bg-red-700 text-white rounded-lg hover:bg-red-800 disabled:opacity-50"
            >
              Confirm Reject
            </button>
            <button
              onClick={onCancelReject}
              className="py-1.5 px-3 text-xs bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
